/**
 * Testi italiani delle pagine di accesso e della verifica in due passaggi.
 *
 * `errors.invalidCredentials` è **un messaggio solo** per utente inesistente,
 * password errata e codice errato: differenziarlo trasformerebbe la pagina di
 * accesso in un elenco di nomi utente validi. Vale come vincolo di sicurezza,
 * non come scelta di stile, e vale identico in ogni lingua
 * (`testi-ui-italiano` §3.5).
 */

/** Testi dell'area di autenticazione. */
export const auth = {
  /** Pagina di accesso. */
  login: {
    /** Frase sotto il marchio. */
    intro: 'Accedi con le tue credenziali.',
    /** Etichetta del campo del nome utente. */
    username: 'Nome utente',
    /** Etichetta del campo della password. */
    password: 'Password',
    /** Pulsante di invio delle credenziali. */
    submit: 'Accedi',
    /** Collegamento alla pagina della password dimenticata. */
    forgot: 'Password dimenticata?',
  },

  /** Collegamenti a informativa e termini, pubblicati sulla landing (ADR 0033). */
  legal: {
    privacy: 'Privacy',
    terms: 'Termini di servizio',
    privacyUrl: 'https://institorai.com/privacy.html',
    termsUrl: 'https://institorai.com/termini.html',
  },

  /** Accesso e registrazione con Google (ADR 0033). */
  google: {
    /** Frase sotto il marchio quando Google è acceso. */
    intro: 'Entra o crea un account con Google.',
    /** Pulsante che porta a Google. */
    continue: 'Continua con Google',
    /** Collegamento che apre il modulo con la password, solo per gli amministratori. */
    adminLogin: 'Accesso amministratori',
    errors: {
      cancelled: 'Accesso con Google annullato.',
      expired: 'Tempo scaduto. Riprova.',
      failed: 'Accesso con Google non riuscito. Riprova.',
      unavailable: 'Servizio non disponibile. Riprova tra poco.',
    },
  },

  /** Password dimenticata (ADR 0030 §6.4). */
  forgot: {
    /** Titolo. */
    title: 'Password dimenticata',
    /** Spiegazione con il recupero acceso. */
    intro: 'Scrivi il tuo nome utente. Se l’account ha un indirizzo email, ti mandiamo un collegamento per scegliere una nuova password.',
    /** Recupero spento. */
    disabled: 'Il recupero via email non è attivo. Chiedi a un amministratore di reimpostare la tua password.',
    /** Lettura della configurazione. */
    checking: 'Verifica del recupero…',
    /** Campo. */
    username: 'Nome utente',
    /** Pulsante. */
    submit: 'Invia collegamento',
    /** Esito, uguale per ogni nome. */
    sent: 'Se il nome utente esiste e ha un indirizzo email, il collegamento arriva fra pochi minuti. Vale un’ora.',
    /** Errore generico. */
    failed: 'Richiesta non inviata. Riprova.',
    /** Ritorno all'accesso. */
    back: 'Torna all’accesso',
  },

  /** Registrazione del secondo fattore. */
  enroll: {
    /** Titolo della pagina. */
    title: 'Attiva la verifica in due passaggi',
    /** Istruzioni sopra il codice QR. */
    intro:
      'Inquadra il codice QR con la tua app di autenticazione. Poi digita il codice a sei cifre che compare.',
    /** Attesa del codice QR dal gateway. */
    preparing: 'Preparazione del codice QR…',
    /** Testo alternativo del codice QR: dice a che cosa serve l'immagine. */
    qrAlt: 'Codice QR per registrare {{account}} su {{issuer}}',
    /** Introduzione alla chiave da digitare quando il QR non è inquadrabile. */
    manualKey: "Se non puoi inquadrare il QR, inserisci questa chiave nell'app.",
    /** Ricaduta quando la chiave non si ricava dall'indirizzo di registrazione. */
    manualKeyMissing: 'Chiave manuale non disponibile: usa il codice QR.',
    /** Etichetta del campo del codice. */
    code: 'Codice a sei cifre',
    /** Pulsante che conferma la registrazione. */
    submit: 'Conferma registrazione',
    /** Uscita dalla registrazione volontaria, aperta dal profilo. */
    cancel: 'Non ora',
  },

  /** Verifica del secondo fattore. */
  verify: {
    /** Titolo della pagina. */
    title: 'Verifica in due passaggi',
    /** Istruzioni quando si attende il codice dell'app. */
    introTotp: 'Inserisci il codice a sei cifre generato dalla tua app di autenticazione.',
    /** Istruzioni quando si attende un codice di recupero. */
    introRecovery:
      'Inserisci uno dei codici di recupero che hai salvato. Ogni codice vale una volta sola.',
    /** Etichetta del campo del codice dell'app. */
    code: 'Codice a sei cifre',
    /** Etichetta del campo del codice di recupero. */
    recoveryCode: 'Codice di recupero',
    /** Pulsante di invio della credenziale. */
    submit: 'Verifica',
    /** Comando che torna al codice dell'app. */
    useApp: 'Usa il codice dell’app',
    /** Comando che passa ai codici di recupero. */
    useRecovery: 'Usa un codice di recupero',
    /** Comando che chiude la sessione in attesa. */
    back: 'Torna all’accesso',
  },

  /** Codici di recupero, mostrati una sola volta. */
  recovery: {
    /** Titolo della pagina. */
    title: 'Codici di recupero',
    /** Titolo dell'avviso sopra l'elenco. */
    warningTitle: 'Questi codici compaiono una sola volta',
    /** Corpo dell'avviso. */
    warningBody:
      'Conservali in un posto sicuro: servono ad accedere se perdi il dispositivo. Ogni codice vale per un solo accesso.',
    /** Pulsante che copia i codici. */
    copy: 'Copia negli appunti',
    /** Esito riuscito della copia. */
    copied: 'Codici copiati.',
    /** Esito fallito della copia, con l'alternativa. */
    copyFailed: 'Copia non riuscita: trascrivili a mano.',
    /** Casella con cui si dichiara di aver messo al sicuro i codici. */
    saved: 'Li ho salvati in un posto sicuro',
    /** Pulsante che prosegue verso la dashboard. */
    continue: 'Continua',
  },

  /** Messaggi d'errore dell'autenticazione. */
  errors: {
    /** Messaggio unico per ogni credenziale rifiutata. Non si differenzia mai. */
    invalidCredentials: 'Credenziali non valide.',
    /** Troppi tentativi, senza attesa dichiarata dal gateway. */
    tooManyAttempts: 'Troppi tentativi. Riprova più tardi.',
    /** Troppi tentativi, attesa di un secondo. */
    tooManyAttemptsIn_one: 'Troppi tentativi. Riprova fra {{count, number}} secondo.',
    /** Troppi tentativi, attesa di più secondi. */
    tooManyAttemptsIn_other: 'Troppi tentativi. Riprova fra {{count, number}} secondi.',
    /**
     * Nome utente mancante all'invio del modulo di accesso.
     *
     * Il pulsante di invio non si disabilita in base ai campi (l'autocompletamento
     * del browser non genera eventi React e lo lascerebbe bloccato): si valida
     * qui, all'invio, non prima.
     */
    usernameRequired: 'Inserisci il nome utente.',
    /** Password mancante all'invio del modulo di accesso. */
    passwordRequired: 'Inserisci la password.',
    /** Codice mancante o incompleto all'invio della verifica in due passaggi. */
    codeRequired: 'Inserisci il codice.',
  },
};

/** Forma dei testi di autenticazione, usata per tipare le altre lingue. */
export type Auth = typeof auth;
